import { apiClient } from '../api/apiClient';

export interface VideoCallRequest {
  id: number;
  patient_id: number;
  doctor_id: number;
  channel_name: string;
  status: string;
  appointment_id?: number;
  auto_created?: boolean;
  initiated_at: string;
  answered_at?: string;
  ended_at?: string;
  duration_seconds?: number;
}

export interface TwilioConfig {
  configured: boolean;
  video_call_room_create_buffer_minutes: number;
  video_call_join_grace_minutes: number;
}

export interface TwilioTokenResponse {
  token: string;
  room_name: string;
  identity: string;
}

export interface ClinicalDataResponse {
  summary: string;
  summary_edited: boolean;
  summary_edited_at?: string;
  summary_edited_by?: number;
  prescriptions: Array<{
    id: string;
    medication_name: string;
    dosage?: string;
    frequency?: string;
    instructions?: string;
    duration?: string;
    prescribed_by?: string;
    prescribed_at?: string;
    edited_at?: string;
    edited_by?: number;
  }>;
  lab_tests: Array<{
    id: number;
    test_name: string;
    test_category?: string;
    reason?: string;
    priority?: string;
    instructions?: string;
    notes?: string;
    ordered_at?: string;
    edited_at?: string;
    edited_by?: number;
  }>;
}

export interface VideoCallChatMessage {
  id: number;
  content: string;
  sender_type: string;
  recipient_type?: string;
  sender_id?: number;
  role: string;
  created_at?: string;
  is_editable?: boolean;
  is_deletable?: boolean;
}

export interface VideoCallChatSession {
  session_id: number;
  call_id: number;
  user_id: number;
  doctor_id: number;
}

export interface CanJoinCallResponse {
  can_join: boolean;
  message?: string;
}

class VideoConferenceService {
  /**
   * Get video call request for an appointment
   */
  async getCallRequestForAppointment(appointmentId: number): Promise<VideoCallRequest | null> {
    try {
      const response = await apiClient.get<VideoCallRequest>(
        `/api/v1/video/call-requests/appointment/${appointmentId}`
      );
      return response.data;
    } catch (err: any) {
      if (err.response?.status === 404) {
        return null;
      }
      throw err;
    }
  }

  /**
   * Get Twilio configuration
   */
  async getTwilioConfig(): Promise<TwilioConfig> {
    const response = await apiClient.get<TwilioConfig>('/api/v1/video/twilio/config');
    return response.data;
  }

  /**
   * Create Twilio access token
   */
  async createTwilioToken(roomName: string, identity: string): Promise<TwilioTokenResponse> {
    const response = await apiClient.post<TwilioTokenResponse>('/api/v1/video/twilio/token', {
      room_name: roomName,
      identity: identity,
    });
    return response.data;
  }

  /**
   * Update video call status
   */
  async updateCallStatus(
    callId: number,
    status: string,
    endReason?: string
  ): Promise<VideoCallRequest> {
    const body: any = { status };
    if (endReason) {
      body.end_reason = endReason;
    }
    const response = await apiClient.patch<VideoCallRequest>(
      `/api/v1/video/call-requests/${callId}/status`,
      body
    );
    return response.data;
  }

  /**
   * Start recording for a video call
   */
  async startRecording(callId: number): Promise<any> {
    const response = await apiClient.post(`/api/v1/video/call/${callId}/start-recording`);
    return response.data;
  }

  /**
   * Stop recording for a video call
   */
  async stopRecording(callId: number): Promise<any> {
    const response = await apiClient.post(`/api/v1/video/call/${callId}/stop-recording`);
    return response.data;
  }

  /**
   * Get clinical data for a video call
   */
  async getClinicalData(callId: number): Promise<ClinicalDataResponse> {
    const response = await apiClient.get<ClinicalDataResponse>(
      `/api/v1/video/call/${callId}/clinical`
    );
    return response.data;
  }

  /**
   * Update clinical summary
   */
  async updateClinicalSummary(callId: number, summary: string): Promise<any> {
    const response = await apiClient.put(`/api/v1/video/call/${callId}/clinical/summary`, {
      summary,
    });
    return response.data;
  }

  /**
   * Create or get chat session for video call
   */
  async createOrGetChatSession(callId: number): Promise<VideoCallChatSession> {
    const response = await apiClient.post<VideoCallChatSession>(
      `/api/v1/video/call/${callId}/chat/session`
    );
    return response.data;
  }

  /**
   * Get chat messages for video call
   */
  async getChatMessages(callId: number): Promise<{ messages: VideoCallChatMessage[] }> {
    const response = await apiClient.get<{ messages: VideoCallChatMessage[] }>(
      `/api/v1/video/call/${callId}/chat/messages`
    );
    return response.data;
  }

  /**
   * Send chat message
   */
  async sendChatMessage(
    callId: number,
    content: string,
    recipientType: string,
    senderType: string
  ): Promise<any> {
    const response = await apiClient.post(`/api/v1/video/call/${callId}/chat/message`, {
      content,
      recipient_type: recipientType,
      sender_type: senderType,
    });
    return response.data;
  }

  /**
   * Check if a video call can be joined for an appointment
   */
  async canJoinCallForAppointment(appointmentId: number): Promise<CanJoinCallResponse> {
    const response = await apiClient.get<CanJoinCallResponse>(
      `/api/v1/video/call-requests/appointment/${appointmentId}/can-join`
    );
    return response.data;
  }

  /**
   * Update a prescription
   */
  async updatePrescription(
    callId: number,
    prescriptionId: string,
    updateData: {
      medication_name?: string;
      dosage?: string;
      frequency?: string;
      instructions?: string;
      duration?: string;
    }
  ): Promise<any> {
    const response = await apiClient.put(
      `/api/v1/video/call/${callId}/clinical/prescription/${prescriptionId}`,
      updateData
    );
    return response.data;
  }

  /**
   * Delete a prescription
   */
  async deletePrescription(callId: number, prescriptionId: string): Promise<any> {
    const response = await apiClient.delete(
      `/api/v1/video/call/${callId}/clinical/prescription/${prescriptionId}`
    );
    return response.data;
  }

  /**
   * Update a lab test
   */
  async updateLabTest(
    callId: number,
    labTestId: number,
    updateData: {
      test_name?: string;
      test_category?: string;
      reason?: string;
      priority?: string;
      instructions?: string;
      notes?: string;
    }
  ): Promise<any> {
    const response = await apiClient.put(
      `/api/v1/video/call/${callId}/clinical/lab-test/${labTestId}`,
      updateData
    );
    return response.data;
  }

  /**
   * Delete a lab test
   */
  async deleteLabTest(callId: number, labTestId: number): Promise<any> {
    const response = await apiClient.delete(
      `/api/v1/video/call/${callId}/clinical/lab-test/${labTestId}`
    );
    return response.data;
  }

  /**
   * Create a new prescription
   */
  async createPrescription(
    callId: number,
    prescriptionData: {
      medication_name: string;
      dosage?: string;
      frequency?: string;
      instructions?: string;
      duration?: string;
    }
  ): Promise<any> {
    const response = await apiClient.post(
      `/api/v1/video/call/${callId}/clinical/prescription`,
      prescriptionData
    );
    return response.data;
  }

  /**
   * Create a new lab test
   */
  async createLabTest(
    callId: number,
    labTestData: {
      test_name: string;
      test_category?: string;
      reason?: string;
      priority?: string;
      instructions?: string;
      notes?: string;
    }
  ): Promise<any> {
    const response = await apiClient.post(
      `/api/v1/video/call/${callId}/clinical/lab-test`,
      labTestData
    );
    return response.data;
  }
}

export const videoConferenceService = new VideoConferenceService();

